import { useState, useEffect, useCallback, useRef } from 'react';
import { geocodeAddress, normalizeResult } from './geocode';
import type { GeocodeData, GeocodeInput } from './places.types';

// ============================================================================
// Types
// ============================================================================

type GeocodeOptions = Readonly<{
  skip?: boolean;
}>;

type GeocodeState = Readonly<{
  data: GeocodeData | null;
  loading: boolean;
  error: unknown;
  called: boolean;
}>;

// ============================================================================
// Helpers
// ============================================================================

const buildGeocodeParams = (input: GeocodeInput) => {
  const params: Record<string, string> = {};
  if (input.address) params.address = input.address;
  if (input.placeId) params.place_id = input.placeId;
  if (input.location) params.latlng = `${input.location.lat},${input.location.lng}`;
  if (input.components) params.components = input.components;
  if (input.bounds) params.bounds = input.bounds;
  if (input.language) params.language = input.language;
  if (input.region) params.region = input.region;
  return params;
};

// Note: only considers primary geocoding params (address/placeId/location) for dedup.
// Auxiliary params (language, region, bounds, components) are not included in the key —
// no current callsite passes them, but if a future consumer needs to vary by those params,
// this key should be extended.
const getInputKey = (input: GeocodeInput): string => input.address || input.placeId || (input.location ? `${input.location.lat},${input.location.lng}` : '');

const INITIAL_STATE: GeocodeState = {
  data: null,
  loading: false,
  error: null,
  called: false,
};

// ============================================================================
// useGeocode (auto-fire, like useQuery)
// ============================================================================

/**
 * Geocodes an address, placeId, or location using the backend location service.
 * Auto-fires when input changes (unless skip is true).
 *
 * @example
 * ```tsx
 * const { data, loading, error, called } = useGeocode({ address: 'San Francisco' });
 * const { data } = useGeocode({ placeId: 'ChIJ...' });
 * const { data } = useGeocode({ address }, { skip: !shouldGeocode });
 * ```
 */
export const useGeocode = (input: GeocodeInput, options?: GeocodeOptions) => {
  const { skip = false } = options ?? {};
  const [state, setState] = useState<GeocodeState>(INITIAL_STATE);

  const inputKey = getInputKey(input);
  const lastFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (skip || !inputKey || lastFetchedRef.current === inputKey) return;
    lastFetchedRef.current = inputKey;
    let cancelled = false;

    const run = async () => {
      setState(prev => ({ ...prev, loading: true, error: null }));
      try {
        const result = await geocodeAddress(buildGeocodeParams(input));
        if (cancelled) return;
        setState({
          data: result ? normalizeResult(result) : null,
          loading: false,
          error: result ? null : 'No results found',
          called: true,
        });
      } catch (err) {
        if (cancelled) return;
        setState({ data: null, loading: false, error: err, called: true });
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputKey, skip]);

  return state;
};

// ============================================================================
// useGeocodeLazy (imperative, like useLazyQuery)
// ============================================================================

/**
 * Lazy geocoding hook — returns an imperative function to trigger geocoding.
 *
 * @example
 * ```tsx
 * const [geocode, { data, loading, error }] = useGeocodeLazy();
 * const result = await geocode({ placeId: 'ChIJ...' });
 * ```
 */
export const useGeocodeLazy = (): [(input: GeocodeInput) => Promise<GeocodeData | null>, GeocodeState] => {
  const [state, setState] = useState<GeocodeState>(INITIAL_STATE);

  const geocode = useCallback(async (input: GeocodeInput): Promise<GeocodeData | null> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const result = await geocodeAddress(buildGeocodeParams(input));
      const data = result ? normalizeResult(result) : null;
      setState({ data, loading: false, error: data ? null : 'No results found', called: true });
      return data;
    } catch (err) {
      setState({ data: null, loading: false, error: err, called: true });
      return null;
    }
  }, []);

  return [geocode, state];
};
